import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../AuthContext';
import { SERVER_URL } from '../config';
import { CATEGORY_GROUPS, MIN_GOAL_AMOUNT, MAX_GOAL_AMOUNT } from '../utils/constants';
import { uploadImages } from '../utils/uploadImages';
import SortableImageGrid from './SortableImageGrid';
import Avatar from './Avatar';
import CommunityTrustLimits from './CommunityTrustLimits';
import GiftPoolTips from './GiftPoolTips';

function CreatePostForm({ onSuccess, onClose, editPost = null, isModal = false }) {
  const { user, isAuthenticated, token } = useAuth();
  const isEditMode = !!editPost;
  const [formData, setFormData] = useState({
    userEmail: '',
    title: '',
    description: '',
    goalAmount: '',
    category: '', // No default - user must select
    beneficiaryName: 'Self',
    beneficiaryEmail: '',
    relationship: 'friend', // Default relationship
    isTimeSensitive: false,
    visibility: 'everyone', // Default to everyone
    status: 'active', // Default to active
    durationDays: null, // NULL = "open for now" (default)
    imageUrl: '', // Optional image URL
  });
  const [beneficiaryType, setBeneficiaryType] = useState('self'); // 'self' or 'other'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [imageError, setImageError] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientResults, setRecipientResults] = useState([]);
  const [recipientSearching, setRecipientSearching] = useState(false);
  const [selectedRecipient, setSelectedRecipient] = useState(null); // { id, name, email, profile_photo_url, can_receive_payments }
  const [showRecipientDropdown, setShowRecipientDropdown] = useState(false);

  const recipientSearchRef = useRef(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [showVisibilityMenu, setShowVisibilityMenu] = useState(false);
  const visibilityRef = useRef(null);
  const [successInfo, setSuccessInfo] = useState(null); // { isNewRecipient, recipientEmail, recipientName }
  const [showTips, setShowTips] = useState(false);
  const [showTrustLimits, setShowTrustLimits] = useState(false);
  const needsVerification = !!(user && !user.is_verified);

  const VISIBILITY_OPTIONS = [
    {
      value: 'everyone',
      label: 'Everyone',
      description: 'Anyone on Gifted can see this post',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
          <circle cx="10" cy="10" r="7.5"/>
          <ellipse cx="10" cy="10" rx="3.5" ry="7.5"/>
          <line x1="2.5" y1="10" x2="17.5" y2="10"/>
        </svg>
      ),
    },
    {
      value: 'followers',
      label: 'Followers only',
      description: 'Only people who follow you',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5">
          <circle cx="7.5" cy="7" r="2.5"/>
          <circle cx="13" cy="7" r="2.5"/>
          <path d="M2.5 16.5a5 5 0 0 1 10 0" strokeLinecap="round"/>
          <path d="M11 15a5 5 0 0 1 7 1.5" strokeLinecap="round"/>
        </svg>
      ),
    },
    {
      value: 'private',
      label: 'Private',
      description: 'Only people you share the link with',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
          <path fillRule="evenodd" d="M10 1a4.5 4.5 0 0 0-4.5 4.5V9H5a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-6a2 2 0 0 0-2-2h-.5V5.5A4.5 4.5 0 0 0 10 1Zm3 8V5.5a3 3 0 1 0-6 0V9h6Z" clipRule="evenodd" />
        </svg>
      ),
    },
  ];

  // Redirect to onClose if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      onClose();
    }
  }, [isAuthenticated, onClose]);

  // Auto-fill user email if authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setFormData(prev => ({
        ...prev,
        userEmail: user.email,
        beneficiaryEmail: prev.beneficiaryName === 'Self' ? user.email : prev.beneficiaryEmail,
      }));
    }
  }, [isAuthenticated, user]);


  const processFiles = files => {
    // Clear previous image errors
    setImageError('');

    // Validate max 5 images
    if (imageFiles.length + files.length > 5) {
      setImageError('Maximum 5 images allowed per post.');
      return;
    }

    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const validFiles = [];
    const errors = [];

    for (const file of files) {
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        errors.push(
          `${file.name}: Invalid file type. Please upload JPEG, PNG, GIF, or WebP images.`
        );
        continue;
      }

      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        errors.push(`${file.name}: File size exceeds 5MB limit.`);
        continue;
      }

      validFiles.push(file);
    }

    // Show all errors together
    if (errors.length > 0) {
      setImageError(errors.join('\n'));
    }

    // Only process valid files
    if (validFiles.length > 0) {
      const newPreviews = [];
      let loadedCount = 0;

      validFiles.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          newPreviews.push(reader.result);
          loadedCount++;

          // When all files are loaded, update state
          if (loadedCount === validFiles.length) {
            setImagePreviews(prev => [...prev, ...newPreviews]);
            setImageFiles(prev => [...prev, ...validFiles]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const handleImageFilesChange = async e => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    processFiles(files);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = Array.from(e.dataTransfer.files);
    if (files.length === 0) return;
    processFiles(files);
  };

  const removeImage = index => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    setImageError('');
  };

  const moveImage = (from, to) => {
    const files = [...imageFiles];
    const previews = [...imagePreviews];
    const [f] = files.splice(from, 1);
    const [p] = previews.splice(from, 1);
    files.splice(to, 0, f);
    previews.splice(to, 0, p);
    setImageFiles(files);
    setImagePreviews(previews);
  };

  const handleUploadImages = async () => {
    if (imageFiles.length === 0) return [];
    setUploadingImages(true);
    try {
      return await uploadImages(imageFiles, token);
    } catch (err) {
      setError(`Image upload failed: ${err.message}`);
      return [];
    } finally {
      setUploadingImages(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Prevent double submission
    if (loading) return;

    if (!formData.category) {
      setError('Please select a category.');
      return;
    }

    if (beneficiaryType === 'other' && !selectedRecipient) {
      setError('Please search for and select a recipient.');
      return;
    }

    setLoading(true);
    setError('');

    // Upload images if files are selected
    let finalImageUrls = [];
    if (imageFiles.length > 0) {
      const uploadedUrls = await handleUploadImages();
      if (uploadedUrls.length > 0) {
        finalImageUrls = uploadedUrls;
      } else if (uploadingImages) {
        // Upload failed
        setLoading(false);
        return;
      }
    }

    // Use first image as the main image_url for backward compatibility
    const finalImageUrl = finalImageUrls.length > 0 ? finalImageUrls[0] : null;

    // For "self" beneficiary, ensure email is set to user's email
    let beneficiaryEmailToSend = formData.beneficiaryEmail;
    let beneficiaryNameToSend = formData.beneficiaryName;

    if (beneficiaryType === 'self') {
      beneficiaryEmailToSend = formData.userEmail;
      beneficiaryNameToSend = 'Self';
    }

    try {
      const url = isEditMode ? `${SERVER_URL}/posts/${editPost.id}` : `${SERVER_URL}/posts`;
      const method = isEditMode ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({
          user_email: formData.userEmail,
          title: formData.title || formData.description.slice(0, 80).trim(),
          description: formData.description,
          goal_amount: formData.goalAmount ? parseFloat(formData.goalAmount) : null,
          category: formData.category,
          is_time_sensitive: formData.isTimeSensitive,
          beneficiary_name: beneficiaryNameToSend,
          beneficiary_email: beneficiaryEmailToSend,
          relationship: beneficiaryType === 'other' ? formData.relationship : null,
          visibility: formData.visibility,
          status: formData.status,
          duration_days: formData.durationDays,
          image_url: finalImageUrl || null,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        const errorMessage = errorData.detail || 'Failed to create post';

        if (response.status === 403 && errorMessage.includes('verify your email')) {
          setError(errorMessage);
          setLoading(false);
          return;
        }

        if (response.status === 429 || errorMessage.includes('per recipient per 30 days')) {
          setError(errorMessage);
          setLoading(false);
          return;
        }

        throw new Error(errorMessage);
      }

      const data = await response.json();

      // Save additional images to post_images table if we have more than one
      if (finalImageUrls.length > 0) {
        try {
          const imagesResponse = await fetch(`${SERVER_URL}/posts/${data.id}/images`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              image_urls: finalImageUrls,
            }),
          });

          if (!imagesResponse.ok) {
            console.error('Failed to save images to post_images table');
          }
        } catch (err) {
          console.error('Error saving images:', err);
          // Don't fail the post creation if image saving fails
        }
      }

      setSuccessInfo({
        isNewRecipient: beneficiaryType === 'other' && !selectedRecipient?.id,
        recipientEmail: selectedRecipient?.email || '',
        recipientName: selectedRecipient?.username || '',
        post: data,
      });
    } catch (err) {
      setError(err.message);
      console.error('Error creating post:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = e => {
    const { name, value } = e.target;

    // If user email changes and beneficiary is still "Self", update beneficiary email too
    if (name === 'userEmail' && beneficiaryType === 'self') {
      setFormData({
        ...formData,
        userEmail: value,
        beneficiaryEmail: value,
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  const handleBeneficiaryTypeChange = type => {
    setBeneficiaryType(type);
    if (type === 'self') {
      setFormData({
        ...formData,
        beneficiaryName: 'Self',
        beneficiaryEmail: formData.userEmail,
      });
      setSelectedRecipient(null);
      setRecipientSearch('');
      setRecipientResults([]);
    } else {
      setFormData({
        ...formData,
        beneficiaryName: '',
        beneficiaryEmail: '',
      });
      setSelectedRecipient(null);
      setRecipientSearch('');
      setRecipientResults([]);
    }
  };

  const searchRecipientTimeoutRef = useRef(null);

  const handleRecipientSearchChange = e => {
    const q = e.target.value;
    setRecipientSearch(q);
    setSelectedRecipient(null);
    setFormData(prev => ({ ...prev, beneficiaryName: '', beneficiaryEmail: '' }));

    if (searchRecipientTimeoutRef.current) clearTimeout(searchRecipientTimeoutRef.current);

    if (q.trim().length < 2) {
      setRecipientResults([]);
      setShowRecipientDropdown(false);
      return;
    }

    setRecipientSearching(true);
    searchRecipientTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await fetch(`${SERVER_URL}/users/search?q=${encodeURIComponent(q.trim())}`);
        if (response.ok) {
          const data = await response.json();
          const users = data.users || [];
          if (users.length === 0 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(q.trim())) {
            handleSelectRecipient({ id: null, name: q.trim().split('@')[0], email: q.trim(), can_receive_payments: false });
          } else {
            setRecipientResults(users);
            setShowRecipientDropdown(true);
          }
        }
      } catch (err) {
        console.error('Error searching users:', err);
      } finally {
        setRecipientSearching(false);
      }
    }, 800);
  };

  const handleSelectRecipient = recipient => {
    if (recipient.email === user?.email) {
      handleBeneficiaryTypeChange('self');
      setShowRecipientDropdown(false);
      return;
    }
    setSelectedRecipient(recipient);
    setRecipientSearch(recipient.username);
    setShowRecipientDropdown(false);
    setFormData(prev => ({
      ...prev,
      beneficiaryName: recipient.username,
      beneficiaryEmail: recipient.email,
    }));
  };

  const handleClearRecipient = () => {
    setSelectedRecipient(null);
    setRecipientSearch('');
    setRecipientResults([]);
    setShowRecipientDropdown(false);

    setFormData(prev => ({ ...prev, beneficiaryName: '', beneficiaryEmail: '' }));
  };

  // Load post data if in edit mode
  useEffect(() => {
    if (isEditMode && editPost) {
      setFormData({
        userEmail: user?.email || '',
        title: editPost.title,
        description: editPost.description,
        goalAmount: editPost.goal_amount?.toString() || '',
        category: editPost.category,
        isTimeSensitive: editPost.is_time_sensitive || false,
        beneficiaryName: editPost.beneficiary_name || 'Self',
        beneficiaryEmail: editPost.beneficiary_email || user?.email || '',
        visibility: editPost.visibility || 'everyone',
        status: editPost.status || 'active',
        durationDays: editPost.duration_days !== undefined ? editPost.duration_days : null,
        imageUrl: editPost.image_url || '',
      });

      // Note: Existing images from edit mode will need to be re-uploaded
      // as we've removed the URL input option

      if (editPost.beneficiary_name && editPost.beneficiary_name !== 'Self') {
        setBeneficiaryType('other');
        // Pre-populate selected recipient for edit mode display
        setSelectedRecipient({
          id: null,
          name: editPost.beneficiary_name,
          email: editPost.beneficiary_email || '',
          profile_photo_url: null,
          can_receive_payments: true, // assume they connected already
        });
        setRecipientSearch(editPost.beneficiary_name);
      }
    }
  }, [isEditMode, editPost, user]);

  /* ─── SUCCESS SCREEN ────────────────────────────────────────── */
  if (successInfo) {
    const content = (
      <div className="flex flex-col items-center text-center gap-4 py-8 px-6">
        <div className="w-14 h-14 rounded-full bg-green-100 flex items-center justify-center text-2xl">🎉</div>
        <h2 className="text-lg font-semibold text-gray-900">Gift pool created!</h2>
        {successInfo.isNewRecipient ? (
          <p className="text-sm text-gray-600">
            An invite has been sent to <strong>{successInfo.recipientEmail}</strong> to join Gifted and accept the pool. Contributions will open once they accept.
          </p>
        ) : successInfo.recipientName ? (
          <p className="text-sm text-gray-600">
            <strong>{successInfo.recipientName}</strong> has been notified and needs to accept the pool before contributions open.
          </p>
        ) : null}
        <p className="text-sm text-gray-500">You can view the pending post on your profile.</p>
        <button
          type="button"
          onClick={() => onSuccess && onSuccess(successInfo.post)}
          className="mt-2 px-6 py-2 bg-indigo-500 text-white text-sm font-medium rounded-lg hover:bg-indigo-600"
        >
          Done
        </button>
      </div>
    );
    if (isModal) return <div className="px-6 pb-8">{content}</div>;
    return <div className="max-w-2xl mx-auto bg-white">{content}</div>;
  }

  /* ─── MODAL VERSION ─────────────────────────────────────────── */
  if (isModal) {
    return (
      <div className="px-6 pb-8">
        {user && (
          <div className="flex items-center gap-3 py-4">
            <Avatar name={user.username} email={user.email} profilePhotoUrl={user.profile_photo_url} size="w-10 h-10" textSize="text-base" />
            <div>
              <div className="font-semibold text-gray-900 text-sm">{user.username}</div>
              <div className="relative mt-0.5">
                <button
                  type="button"
                  onClick={() => setShowVisibilityMenu(v => !v)}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
                >
                  <span className="[&>svg]:w-3 [&>svg]:h-3">{VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.icon}</span>
                  <span>{VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.label}</span>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-3 h-3">
                    <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                  </svg>
                </button>
                {showVisibilityMenu && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowVisibilityMenu(false)} />
                    <div className="absolute top-full left-0 mt-1 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                      <p className="px-4 pt-3 pb-1 font-semibold text-gray-900 text-sm">Who can see your post?</p>
                      {VISIBILITY_OPTIONS.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => { setFormData(prev => ({ ...prev, visibility: opt.value })); setShowVisibilityMenu(false); }}
                          className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors"
                        >
                          <span className="text-gray-500 flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">{opt.icon}</span>
                          <div className="flex-1 text-left">
                            <div className="font-medium text-gray-900 text-sm">{opt.label}</div>
                            <div className="text-xs text-gray-500">{opt.description}</div>
                          </div>
                          {formData.visibility === opt.value && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-indigo-600 flex-shrink-0">
                              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        <form onSubmit={handleSubmit} className="space-y-4">
          {!isAuthenticated && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Your Email *</label>
              <input type="email" name="userEmail" value={formData.userEmail} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent" placeholder="your.email@example.com" />
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">Who is this gift pool for? *</label>
            <div className="flex gap-4">
              <label className="flex items-center cursor-pointer">
                <input type="radio" name="beneficiaryType" checked={beneficiaryType === 'self'} onChange={() => handleBeneficiaryTypeChange('self')} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" />
                <span className="ml-2 text-gray-700">Self</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input type="radio" name="beneficiaryType" checked={beneficiaryType === 'other'} onChange={() => handleBeneficiaryTypeChange('other')} className="w-4 h-4 text-indigo-600 focus:ring-indigo-500" />
                <span className="ml-2 text-gray-700">Someone else</span>
              </label>
            </div>
          </div>
          {beneficiaryType === 'other' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Recipient *{isEditMode && editPost.current_amount > 0 && <span className="ml-2 text-xs text-gray-500">(Cannot change after receiving donations)</span>}</label>
              <div className="relative" ref={recipientSearchRef}>
                {selectedRecipient ? (
                  <div className="flex items-center gap-3 px-3 py-2.5 border border-green-300 bg-green-50 rounded-lg">
                    <Avatar name={selectedRecipient.username} email={selectedRecipient.email} profilePhotoUrl={selectedRecipient.profile_photo_url} size="w-8 h-8" textSize="text-sm" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{selectedRecipient.username}</div>
                      {!selectedRecipient.id ? <div className="text-xs text-gray-500">Not on Gifted yet · will be invited</div> : selectedRecipient.can_receive_payments ? <div className="text-xs text-green-600">PayPal connected · ready to receive</div> : <div className="text-xs text-indigo-600">PayPal not connected yet</div>}
                    </div>
                    {!(isEditMode && editPost.current_amount > 0) && (
                      <button type="button" onClick={handleClearRecipient} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" /></svg>
                    </div>
                    <input type="text" value={recipientSearch}
                      onChange={handleRecipientSearchChange}
                      onFocus={() => recipientResults.length > 0 && setShowRecipientDropdown(true)}
                      disabled={isEditMode && editPost.current_amount > 0}
                      className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                      placeholder="Search by username or email…"
                      autoComplete="off"
                    />
                    {recipientSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2">
                        <div className="w-4 h-4 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                )}

                {/* Dropdown results */}
                {showRecipientDropdown && recipientResults.length > 0 && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowRecipientDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                      {recipientResults.map(u => (
                        <button
                          key={u.id}
                          type="button"
                          onClick={() => handleSelectRecipient(u)}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors"
                        >
                          <Avatar
                            name={u.username}
                            email={u.email}
                            profilePhotoUrl={u.profile_photo_url}
                            size="w-8 h-8"
                            textSize="text-sm"
                          />
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{u.username}</div>
                            <div className={`text-xs ${u.can_receive_payments ? 'text-green-600' : 'text-indigo-500'}`}>
                              {u.can_receive_payments ? 'PayPal connected' : 'PayPal not connected'}
                            </div>
                          </div>
                          {u.can_receive_payments && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500 flex-shrink-0">
                              <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                            </svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}

                {/* No results — invite fallback */}
                {showRecipientDropdown && recipientResults.length === 0 && recipientSearch.trim().length >= 2 && !recipientSearching && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <p className="text-sm text-gray-500">No results. Try searching by their email address.</p>
                  </div>
                )}
              </div>

              {/* After-selection status */}
              {selectedRecipient && !selectedRecipient.id && (
                <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-800">
                  <span className="font-semibold">{selectedRecipient.username}</span> will receive an invite email to sign up, connect PayPal, and accept the pool. Contributions open once they accept.
                </div>
              )}
              {selectedRecipient && selectedRecipient.id && !selectedRecipient.can_receive_payments && (
                <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded text-sm text-indigo-800">
                  <span className="font-semibold">{selectedRecipient.username}</span> hasn't connected PayPal yet. They'll need to connect it before the pool can accept contributions.
                </div>
              )}
              {selectedRecipient && selectedRecipient.id && selectedRecipient.can_receive_payments && (
                <p className="mt-1 text-xs text-gray-500">
                  They'll be notified and the post goes live once they accept it.
                </p>
              )}
            </div>
          )}


          {/* Title */}
          <input
            type="text"
            name="title"
            value={formData.title}
            onChange={handleChange}
            required
            maxLength={100}
            placeholder="Give your gift pool a title…"
            className="w-full px-1 py-2 text-lg font-semibold placeholder-gray-300 border-none outline-none focus:ring-0 bg-transparent"
          />

          {/* Description */}
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            rows={6}
            className="w-full px-1 py-2 border-none outline-none focus:ring-0 resize-none text-gray-800 placeholder-gray-400 text-base"
            placeholder="Share your story. What do you need, and why?"
          />

          {/* Goal Amount */}
          <div className="flex items-center gap-2 pt-1">
            <span className="text-sm font-medium text-gray-500">Goal</span>
            <div className={`flex items-center border rounded-full px-3 py-1.5 focus-within:border-indigo-400 focus-within:ring-1 focus-within:ring-indigo-400 transition-colors ${isEditMode && editPost.current_amount > 0 ? 'border-gray-200 bg-gray-50' : 'border-gray-300'}`}>
              <span className="text-gray-400 text-sm mr-0.5">$</span>
              <input
                type="number"
                name="goalAmount"
                value={formData.goalAmount}
                onChange={handleChange}
                required
                min={MIN_GOAL_AMOUNT}
                max={MAX_GOAL_AMOUNT}
                step="1"
                disabled={isEditMode && editPost.current_amount > 0}
                className="w-20 border-none outline-none focus:ring-0 text-sm bg-transparent disabled:cursor-not-allowed text-gray-900"
                placeholder="500"
              />
            </div>
            <span className="text-xs text-gray-400">${MIN_GOAL_AMOUNT}–${MAX_GOAL_AMOUNT.toLocaleString()}</span>
            {isEditMode && editPost.current_amount > 0 && <span className="text-xs text-indigo-600">Cannot change after receiving donations</span>}
            {formData.goalAmount && parseFloat(formData.goalAmount) < MIN_GOAL_AMOUNT && <span className="text-xs text-red-600">Min ${MIN_GOAL_AMOUNT}</span>}
            {formData.goalAmount && parseFloat(formData.goalAmount) > MAX_GOAL_AMOUNT && <span className="text-xs text-red-600">Max ${MAX_GOAL_AMOUNT.toLocaleString()}</span>}
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
            <select
              value={formData.category}
              onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Select a category</option>
              {CATEGORY_GROUPS.map(group => (
                <optgroup key={group.label} label={group.label}>
                  {group.items.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Urgent toggle */}
          <div
            className="flex items-center justify-between p-3 rounded-lg border border-gray-200 cursor-pointer select-none hover:bg-gray-50 transition-colors"
            onClick={() => setFormData(prev => ({ ...prev, isTimeSensitive: !prev.isTimeSensitive }))}
          >
            <div>
              <p className="text-sm font-medium text-gray-700">Mark as urgent</p>
              <p className="text-xs text-gray-400">Use this if help is needed right away</p>
            </div>
            <div className={`relative w-10 h-6 rounded-full transition-colors ${formData.isTimeSensitive ? 'bg-red-500' : 'bg-gray-200'}`}>
              <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.isTimeSensitive ? 'translate-x-5' : 'translate-x-1'}`} />
            </div>
          </div>

          {/* Image Upload (Optional) */}
          <div>
            <input
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
              multiple
              onChange={handleImageFilesChange}
              className="hidden"
              id="file-upload"
            />
            <div
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              className={isDragging ? 'rounded-lg ring-2 ring-indigo-400 ring-offset-2' : ''}
            >
              <button
                type="button"
                onClick={() => document.getElementById('file-upload').click()}
                title="Add photos"
                className="flex items-center gap-2 text-sm text-gray-500 hover:text-green-600 transition-colors px-2 py-1 rounded-lg hover:bg-gray-100"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3 3h18M3 21h18M15 6.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
                <span>Add photos</span>
              </button>
            </div>

            {/* Image upload errors - shown right below the upload area */}
            {imageError && (
              <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-700 font-medium mb-1">⚠️ Upload Error</p>
                <p className="text-sm text-red-600 whitespace-pre-line">{imageError}</p>
              </div>
            )}
            {imagePreviews.length > 0 && (
              <div className="mt-3">
                <p className="text-sm font-medium text-gray-700 mb-2">
                  Photos ({imagePreviews.length}/5 max):
                </p>
                <SortableImageGrid
                  items={imagePreviews.map(p => ({ preview: p }))}
                  onReorder={moveImage}
                  onRemove={removeImage}
                />
              </div>
            )}
          </div>

          {/* Collapsible info boxes */}
          <div className="space-y-2 pt-2">
            <div className="border border-amber-200 rounded-xl overflow-hidden">
              <button type="button" onClick={() => setShowTips(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 text-left">
                <span className="font-semibold text-amber-900 text-sm">💡 Tips for a Successful Pool</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-amber-600 transition-transform ${showTips ? 'rotate-180' : ''}`}>
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {showTips && <GiftPoolTips />}
            </div>
            <div className="border border-indigo-100 rounded-xl overflow-hidden">
              <button type="button" onClick={() => setShowTrustLimits(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 text-left">
                <span className="font-semibold text-indigo-900 text-sm">📊 Community Trust Limits</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-indigo-600 transition-transform ${showTrustLimits ? 'rotate-180' : ''}`}>
                  <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
                </svg>
              </button>
              {showTrustLimits && <CommunityTrustLimits />}
            </div>
          </div>

          {/* Visibility + Submit */}
          <div className="pt-4 flex items-center gap-3">
            {/* Visibility pill — only shown in standalone mode; modal shows it near user info */}
            <div className="relative" ref={visibilityRef} style={isModal ? {display:'none'} : {}}>
              <button
                type="button"
                onClick={() => setShowVisibilityMenu(v => !v)}
                className="flex items-center gap-2 px-4 py-3 rounded-full bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 transition-colors text-sm"
              >
                <span className="text-gray-500">
                  {VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.icon}
                </span>
                <span>{VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.label}</span>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
                  <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
                </svg>
              </button>
              {showVisibilityMenu && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setShowVisibilityMenu(false)} />
                  <div className="absolute bottom-full left-0 mb-2 w-72 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-20">
                    <p className="px-4 pt-4 pb-2 font-semibold text-gray-900">Who can see your post?</p>
                    {VISIBILITY_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => { setFormData(prev => ({ ...prev, visibility: opt.value })); setShowVisibilityMenu(false); }}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                      >
                        <span className="text-gray-500 flex-shrink-0">{opt.icon}</span>
                        <div className="flex-1 text-left">
                          <div className="font-medium text-gray-900 text-sm">{opt.label}</div>
                          <div className="text-xs text-gray-500">{opt.description}</div>
                        </div>
                        {formData.visibility === opt.value && (
                          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5 text-indigo-600 flex-shrink-0">
                            <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                          </svg>
                        )}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={loading || uploadingImages || needsVerification}
              className="flex-1 bg-indigo-500 text-white py-3 px-6 rounded-full font-semibold hover:bg-indigo-600 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {uploadingImages
                ? 'Uploading Images...'
                : loading
                  ? isEditMode ? 'Updating...' : 'Creating...'
                  : isEditMode ? 'Update Post' : 'Post'}
            </button>
          </div>
          {needsVerification && <p className="mt-2 text-xs text-gray-500 text-right">Verify your email to post.</p>}
          {error && <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}
        </form>

      </div>
    );
  }

  /* ─── STANDALONE COMPOSE VIEW ─────────────────────────────────── */
  return (
    <div className="max-w-2xl mx-auto bg-white">
      {/* Top bar */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 sticky top-16 bg-white z-10">
        <button type="button" onClick={onClose} className="p-2 rounded-full hover:bg-gray-100 text-gray-500 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
        <div className="flex-1" />
        {/* Visibility dropdown */}
        <div className="relative" ref={visibilityRef}>
          <button type="button" onClick={() => setShowVisibilityMenu(v => !v)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors">
            <span className="[&>svg]:w-4 [&>svg]:h-4 text-gray-500">{VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.icon}</span>
            <span>{VISIBILITY_OPTIONS.find(o => o.value === formData.visibility)?.label}</span>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-gray-400">
              <path fillRule="evenodd" d="M5.22 8.22a.75.75 0 0 1 1.06 0L10 11.94l3.72-3.72a.75.75 0 1 1 1.06 1.06l-4.25 4.25a.75.75 0 0 1-1.06 0L5.22 9.28a.75.75 0 0 1 0-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
          {showVisibilityMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowVisibilityMenu(false)} />
              <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                <p className="px-4 pt-3 pb-1 font-semibold text-gray-900 text-sm">Who can see your post?</p>
                {VISIBILITY_OPTIONS.map(opt => (
                  <button key={opt.value} type="button" onClick={() => { setFormData(prev => ({ ...prev, visibility: opt.value })); setShowVisibilityMenu(false); }} className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition-colors">
                    <span className="text-gray-500 flex-shrink-0 [&>svg]:w-4 [&>svg]:h-4">{opt.icon}</span>
                    <div className="flex-1 text-left">
                      <div className="font-medium text-gray-900 text-sm">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.description}</div>
                    </div>
                    {formData.visibility === opt.value && (
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-600 flex-shrink-0">
                        <path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" />
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
        {/* Post button */}
        <button type="submit" form="gift-pool-form" disabled={loading || uploadingImages || needsVerification} className="bg-green-600 text-white px-5 py-1.5 rounded-full font-semibold text-sm hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed ml-2">
          {uploadingImages ? 'Uploading…' : loading ? (isEditMode ? 'Saving…' : 'Posting…') : (isEditMode ? 'Save' : 'Post')}
        </button>
      </div>
      {error && <div className="mx-4 mt-2 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>}
      {needsVerification && <p className="mx-4 mt-2 text-xs text-gray-500 text-right">Verify your email to post.</p>}

      <form id="gift-pool-form" onSubmit={handleSubmit}>
        {/* Avatar + name + beneficiary toggle */}
        <div className="flex items-start gap-3 px-4 pt-4 pb-2">
          <Avatar name={user?.username} email={user?.email} profilePhotoUrl={user?.profile_photo_url} size="w-10 h-10" textSize="text-base" />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-gray-900">{user?.username}</div>
            <div className="flex items-center gap-1 mt-1 flex-wrap">
              <span className="text-xs text-gray-500">Starting a pool for</span>
              <button type="button" onClick={() => handleBeneficiaryTypeChange('self')} className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${beneficiaryType === 'self' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}>myself</button>
              <span className="text-xs text-gray-300">·</span>
              <button type="button" onClick={() => handleBeneficiaryTypeChange('other')} className={`text-xs px-2 py-0.5 rounded-full font-medium transition-colors ${beneficiaryType === 'other' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-600'}`}>someone else</button>
            </div>
          </div>
        </div>

        {/* Recipient search when "other" */}
        {beneficiaryType === 'other' && (
          <div className="px-4 pb-3">
            <div className="ml-[52px]">
              <div className="relative" ref={recipientSearchRef}>
                {selectedRecipient ? (
                  <div className="flex items-center gap-2 px-3 py-2 border border-green-200 bg-green-50 rounded-xl">
                    <Avatar name={selectedRecipient.username} email={selectedRecipient.email} profilePhotoUrl={selectedRecipient.profile_photo_url} size="w-7 h-7" textSize="text-xs" />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-gray-900 text-sm">{selectedRecipient.username}</div>
                      <div className="text-xs">{!selectedRecipient.id ? <span className="text-gray-500">Not on Gifted yet · will be invited</span> : selectedRecipient.can_receive_payments ? <span className="text-green-600">PayPal connected</span> : <span className="text-indigo-600">PayPal not connected</span>}</div>
                    </div>
                    {!(isEditMode && editPost.current_amount > 0) && (
                      <button type="button" onClick={handleClearRecipient} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" /></svg>
                      </button>
                    )}
                  </div>
                ) : (
                  <div className="relative">
                    <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4"><path fillRule="evenodd" d="M9 3.5a5.5 5.5 0 1 0 0 11 5.5 5.5 0 0 0 0-11ZM2 9a7 7 0 1 1 12.452 4.391l3.328 3.329a.75.75 0 1 1-1.06 1.06l-3.329-3.328A7 7 0 0 1 2 9Z" clipRule="evenodd" /></svg>
                    </div>
                    <input type="text" value={recipientSearch} onChange={handleRecipientSearchChange} onFocus={() => recipientResults.length > 0 && setShowRecipientDropdown(true)} disabled={isEditMode && editPost.current_amount > 0} className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-green-400 focus:border-green-400 disabled:bg-gray-100 disabled:cursor-not-allowed" placeholder="Search by username or email…" autoComplete="off" />
                    {recipientSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2"><div className="w-4 h-4 border-2 border-green-500 border-t-transparent rounded-full animate-spin" /></div>}
                  </div>
                )}
                {showRecipientDropdown && recipientResults.length > 0 && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={() => setShowRecipientDropdown(false)} />
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-20">
                      {recipientResults.map(u => (
                        <button key={u.id} type="button" onClick={() => handleSelectRecipient(u)} className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 transition-colors">
                          <Avatar name={u.username} email={u.email} profilePhotoUrl={u.profile_photo_url} size="w-8 h-8" textSize="text-sm" />
                          <div className="flex-1 text-left min-w-0">
                            <div className="font-medium text-gray-900 text-sm">{u.username}</div>
                            <div className={`text-xs ${u.can_receive_payments ? 'text-green-600' : 'text-indigo-500'}`}>{u.can_receive_payments ? 'PayPal connected' : 'PayPal not connected'}</div>
                          </div>
                          {u.can_receive_payments && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-green-500 flex-shrink-0"><path fillRule="evenodd" d="M16.704 4.153a.75.75 0 0 1 .143 1.052l-8 10.5a.75.75 0 0 1-1.127.075l-4.5-4.5a.75.75 0 0 1 1.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 0 1 1.05-.143Z" clipRule="evenodd" /></svg>
                          )}
                        </button>
                      ))}
                    </div>
                  </>
                )}
                {showRecipientDropdown && recipientResults.length === 0 && recipientSearch.trim().length >= 2 && !recipientSearching && (
                  <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-xl">
                    <p className="text-sm text-gray-500">No results. Try searching by their email address.</p>
                  </div>
                )}
              </div>
              {selectedRecipient && !selectedRecipient.id && (
                <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
                  <span className="font-semibold">{selectedRecipient.username}</span> will receive an invite email to sign up, connect PayPal, and accept the pool. Contributions open once they accept.
                </div>
              )}
              {selectedRecipient && selectedRecipient.id && !selectedRecipient.can_receive_payments && (
                <div className="mt-2 p-2 bg-indigo-50 border border-indigo-200 rounded-lg text-sm text-indigo-800">
                  <span className="font-semibold">{selectedRecipient.username}</span> hasn't connected PayPal yet. They'll need to connect it before the pool can accept contributions.
                </div>
              )}
              {selectedRecipient && selectedRecipient.id && selectedRecipient.can_receive_payments && (
                <p className="mt-1 text-xs text-gray-500">They'll be notified and the post goes live once they accept it.</p>
              )}
              {selectedRecipient && (
                <div className="mt-2">
                  <select name="relationship" value={formData.relationship} onChange={handleChange} required className="w-full px-3 py-2 border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-green-400 focus:border-green-400 bg-white text-gray-700">
                    <option value="acquaintance">Acquaintance</option>
                    <option value="aunt">Aunt</option>
                    <option value="brother">Brother</option>
                    <option value="brother-in-law">Brother-in-law</option>
                    <option value="colleague">Colleague</option>
                    <option value="community member">Community member</option>
                    <option value="cousin">Cousin</option>
                    <option value="daughter">Daughter</option>
                    <option value="daughter-in-law">Daughter-in-law</option>
                    <option value="family member">Family member</option>
                    <option value="father">Father</option>
                    <option value="father-in-law">Father-in-law</option>
                    <option value="friend">Friend</option>
                    <option value="godchild">Godchild</option>
                    <option value="godparent">Godparent</option>
                    <option value="grandfather">Grandfather</option>
                    <option value="grandmother">Grandmother</option>
                    <option value="mother">Mother</option>
                    <option value="mother-in-law">Mother-in-law</option>
                    <option value="neighbor">Neighbor</option>
                    <option value="nephew">Nephew</option>
                    <option value="niece">Niece</option>
                    <option value="other">Other</option>
                    <option value="pastor">Pastor</option>
                    <option value="sister">Sister</option>
                    <option value="sister-in-law">Sister-in-law</option>
                    <option value="son">Son</option>
                    <option value="son-in-law">Son-in-law</option>
                    <option value="spouse">Spouse</option>
                    <option value="stepbrother">Stepbrother</option>
                    <option value="stepdaughter">Stepdaughter</option>
                    <option value="stepfather">Stepfather</option>
                    <option value="stepmother">Stepmother</option>
                    <option value="stepsister">Stepsister</option>
                    <option value="stepson">Stepson</option>
                    <option value="uncle">Uncle</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-400">Your relationship to them (used in their notification)</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Title — no label, large placeholder */}
        <input type="text" name="title" value={formData.title} onChange={handleChange} required maxLength={100} placeholder={isEditMode ? 'Gift pool title…' : 'Give your gift pool a title…'} className="w-full px-4 py-2 text-xl font-semibold placeholder-gray-300 border-none outline-none focus:ring-0 bg-transparent" />

        {/* Description — compose textarea */}
        <textarea name="description" value={formData.description} onChange={handleChange} required rows={6} placeholder="Tell your story. What do you need, and why? Be specific and authentic — this helps your neighbors connect." className="w-full px-4 py-2 placeholder-gray-400 border-none outline-none focus:ring-0 resize-none bg-transparent" />

        {/* Goal amount — inline pill */}
        <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
          <span className="text-sm text-gray-500 font-medium">Goal</span>
          <div className={`flex items-center border rounded-full px-3 py-1.5 focus-within:border-green-400 focus-within:ring-1 focus-within:ring-green-400 transition-colors ${isEditMode && editPost.current_amount > 0 ? 'border-gray-200 bg-gray-50' : 'border-gray-300'}`}>
            <span className="text-gray-400 text-sm mr-0.5">$</span>
            <input type="number" name="goalAmount" value={formData.goalAmount} onChange={handleChange} required min={MIN_GOAL_AMOUNT} max={MAX_GOAL_AMOUNT} step="1" disabled={isEditMode && editPost.current_amount > 0} placeholder="500" className="w-20 border-none outline-none focus:ring-0 text-sm bg-transparent disabled:cursor-not-allowed text-gray-900" />
          </div>
          <span className="text-xs text-gray-400">${MIN_GOAL_AMOUNT}–${MAX_GOAL_AMOUNT.toLocaleString()}</span>
          {isEditMode && editPost.current_amount > 0 && <span className="text-xs text-indigo-600">Cannot change after receiving donations</span>}
          {formData.goalAmount && parseFloat(formData.goalAmount) < MIN_GOAL_AMOUNT && <span className="text-xs text-red-600">Min ${MIN_GOAL_AMOUNT}</span>}
          {formData.goalAmount && parseFloat(formData.goalAmount) > MAX_GOAL_AMOUNT && <span className="text-xs text-red-600">Max ${MAX_GOAL_AMOUNT.toLocaleString()}</span>}
        </div>

        {/* Category */}
        <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-3">
          <span className="text-sm font-medium text-gray-500 whitespace-nowrap">Category <span className="text-red-400 font-normal">*</span></span>
          <select
            value={formData.category}
            onChange={e => setFormData(prev => ({ ...prev, category: e.target.value }))}
            className="flex-1 border border-gray-200 rounded-lg px-2 py-1.5 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-green-400 focus:border-green-400 bg-white"
          >
            <option value="">Select…</option>
            {CATEGORY_GROUPS.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        {/* Urgent toggle (modal) */}
        <div
          className="px-4 py-2 border-t border-gray-100 flex items-center justify-between cursor-pointer select-none hover:bg-gray-50 transition-colors"
          onClick={() => setFormData(prev => ({ ...prev, isTimeSensitive: !prev.isTimeSensitive }))}
        >
          <span className="text-sm font-medium text-gray-500">Urgent</span>
          <div className={`relative w-9 h-5 rounded-full transition-colors ${formData.isTimeSensitive ? 'bg-red-500' : 'bg-gray-200'}`}>
            <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${formData.isTimeSensitive ? 'translate-x-4' : 'translate-x-0.5'}`} />
          </div>
        </div>

        {/* Image previews */}
        {imagePreviews.length > 0 && (
          <div className="px-4 pb-3">
            <SortableImageGrid
              items={imagePreviews.map(p => ({ preview: p }))}
              onReorder={moveImage}
              onRemove={removeImage}
            />
          </div>
        )}

        {/* Image error */}
        {imageError && (
          <div className="mx-4 mb-3 p-3 bg-red-50 border border-red-200 rounded-xl">
            <p className="text-sm text-red-700 font-medium mb-0.5">Upload Error</p>
            <p className="text-sm text-red-600 whitespace-pre-line">{imageError}</p>
          </div>
        )}

        {/* Collapsible info boxes */}
        <div className="px-4 pb-2 space-y-2">
          <div className="border border-amber-200 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowTips(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-amber-50 text-left">
              <span className="font-semibold text-amber-900 text-sm">💡 Tips for a Successful Pool</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-amber-600 transition-transform ${showTips ? 'rotate-180' : ''}`}>
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {showTips && <GiftPoolTips />}
          </div>
          <div className="border border-indigo-100 rounded-xl overflow-hidden">
            <button type="button" onClick={() => setShowTrustLimits(v => !v)} className="w-full flex items-center justify-between px-4 py-3 bg-indigo-50 text-left">
              <span className="font-semibold text-indigo-900 text-sm">📊 Community Trust Limits</span>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-indigo-600 transition-transform ${showTrustLimits ? 'rotate-180' : ''}`}>
                <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
              </svg>
            </button>
            {showTrustLimits && <CommunityTrustLimits />}
          </div>
        </div>

        {/* Bottom toolbar */}
        <div className="border-t border-gray-100 flex items-center px-4 py-2 gap-2">
          <input type="file" accept="image/jpeg,image/jpg,image/png,image/gif,image/webp" multiple onChange={handleImageFilesChange} className="hidden" id="file-upload" />
          <button type="button" onClick={() => document.getElementById('file-upload').click()} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors" title="Add images">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
              <path strokeLinecap="round" strokeLinejoin="round" d="m2.25 15.75 5.159-5.159a2.25 2.25 0 0 1 3.182 0l5.159 5.159m-1.5-1.5 1.409-1.409a2.25 2.25 0 0 1 3.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H3.75A1.5 1.5 0 0 0 2.25 6v12a1.5 1.5 0 0 0 1.5 1.5Zm10.5-11.25h.008v.008h-.008V8.25Zm.375 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Z" />
            </svg>
          </button>
          <span className="text-xs text-gray-400">{imagePreviews.length}/5 photos</span>
        </div>
      </form>

    </div>
  );
}

export default CreatePostForm;
